import _ from 'lodash';
import {
  createSelector
} from 'reselect';
import {
  List,
  Map,
  fromJS
} from 'immutable';
import {
  flow,
  mapValues,
  groupBy,
  filter,
  values
} from 'lodash/fp';

import parameterMessages from 'domain/Options/parameterMessages';
import itemListMessages from 'pages/ItemListPage/messages';
import splashbackMessages from 'domain/Options/generators/messages/splashbackMessages';
import {
  WALL_EDGING_STRIP_ID,
  WALL_EDGING_STRIP_CUSTOMIZABLE_ID,
  WALL_PANEL_ID,
  NONE
} from './generators/splashbacks';
import getItemMeasurement from 'domain/Options/generators/utils/getItemMeasurement';
import {
  NO_KITCHEN_ISLAND_ID
} from 'domain/Options/generators/kitchenIslands';
import {
  NO_WORKTOP_MATERIAL
} from 'domain/Options/generators/materials';
import {
  NO_OVERHANG_ID,
  WATERFALL_WITH_OVERHANG_ID_45_DEGREES,
  WATERFALL_ID_45_DEGREES,
  WATERFALL_ID_90_DEGREES,
  WATERFALL_WITH_OVERHANG_ID_90_DEGREES,
  ROUNDED_OVERHANG_ID,
  HALF_ROUNDED_OVERHANG_ID,
  EMERALD_ID,
  HALF_EMERALD_ID,
  ARC_ID
} from 'domain/Options/generators/overhangs';
import {
  worktopWithOverhangMeasurementsSelector,
  kitchenIslandWithOverhangMeasurementsSelector
} from 'domain/Options/selectors/overhangMeasurements';
import {
  PictoSingleCuts,
  PictoInnerRounding,
  PictoRoundCorner,
  PictoTripleCut,
  PictoHighCabinetJoint,
  PictoDoubleCut,
  PictoCutoutsHob,
  PictoTapHole,
  PictoDrainerGrooves,
  PictoWallPanelRoundSmall,
  PictoWallPanelRoundBig,
  PictoWallPanelSquare,
  PictoWallPanelSingle,
  PictoWallPanelDouble,
  PictoWallPanelTriple,
  PictoFreestandingHob,
} from 'images';

import {
  configurationSelector,
} from './selectors';

import {
  fasteningTypes
} from './generators/sinks';

import {
  calculateWallEdgingStripLength,
  calculateWallPanelLength
} from './splashback';
import {
  shapeInvalidSelector,
  wallPanelHeightInvalidSelector,
  wallEdgingStripCustomizableHeightInvalidSelector,
  kitchenIslandInvalidSelector
} from './selectors/errors';
import {
  selectedThicknessSelector,
  selectedEdgeSelector,
  plannedShapeParameterSelector,
  materialParameterSelector,
  wallLocationParameterSelector,
  kitchenIslandWallLocationParameterSelector,
  sinkMaterialParameterSelector,
  sinkModelParameterSelector,
  sinkFasteningParameterSelector,
  drainerGroovesParameterSelector,
  splashbackParameterSelector,
  kitchenIslandSplashbackSelector,
  wallPanelMaterialParameterSelector,
  wallPanelExpressionParameterSelector,
  sinkSelector,
  sinkUISelector,
  kitchenIslandParameterSelector,
  kitchenIslandMaterialParameterSelector,
  selectedKitchenIslandThicknessSelector,
  selectedKitchenIslandEdgeSelector,
  expressionParameterSelector,
  kitchenIslandExpressionParameterSelector,
  doubleSidedSelector,
  overhangParameterSelector,
  overhangSideParameterSelector,
  kitchenIslandDoubleSidedSelector,
  kitchenIslandOverhangSideParameterSelector,
  kitchenIslandOverhangParameterSelector,
  kitchenIslandWithMeasurementsSelector,
  wallEdgingStripMaterialParameterSelector,
  wallEdgingStripMaterialCustomizableParameterSelector,
  coverPanelParameterSelector,
  selectedCoverPanelThicknessSelector,
  kitchenIslandCoverPanelParameterSelector,
  selectedKitchenIslandCoverPanelThicknessSelector,
} from './selectors/parameters';
import {
  maxDepthSelector,
  wallPanelMaxLengthSelector,
  wallPanelHeightSelector,
  wallEdgingStripHeightSelector,
} from './selectors/measurements';
import {
  cutoutsSelector,
  singleCutsSelector,
  doubleCutsSelector,
  tripleCutsSelector,
  highCabinetJointsSelector,
  roundedCornersSelector,
  invertedCornersSelector,
  tapHolesSelector,
  wallPanelRoundCutsSmallSelector,
  wallPanelRoundCutsBigSelector,
  wallPanelSquareCutsSelector,
  wallPanelSingleCutsSelector,
  wallPanelDoubleCutsSelector,
  wallPanelTripleCutsSelector,
  freestandingHobsSelector,
  kitchenIslandSingleCutsSelector,
  kitchenIslandRoundedCornersSelector,
  kitchenIslandSquareCutsSelector,
} from './selectors/operations';
import {
  calculateWorktopSections,
  calculateKitchenIslandSections,
  calculateWaterfallSections
} from './sections';
import {
  resetCutouts,
  resetSingleCuts,
  resetDoubleCuts,
  resetTripleCuts,
  resetHighCabinetJoints,
  resetRoundedCorners,
  resetInvertedCorners,
  resetTapHoles,
  selectDrainerGrooves,
  selectSinkMaterial,
  selectSplashback,
  resetWallPanelRoundCutsSmall,
  resetWallPanelRoundCutsBig,
  resetWallPanelSquareCuts,
  resetWallPanelSingleCuts,
  resetWallPanelDoubleCuts,
  resetWallPanelTripleCuts,
  selectKitchenIsland,
  resetFreestandingHobs,
  resetKitchenIslandSingleCuts,
  resetKitchenIslandRoundedCorners,
  resetKitchenIslandSquareCuts,
} from './actions';
import {
  calculateFrontEdgeLength,
  calculateVisibleEdgeLength,
  calculateKitchenIslandVisibleEdgeLength,
  calculateWaterfallEdgeLength,
  calculateArcOverhangLength,
  calculateArcOverhangFrontEdgeLengthToRemove
} from './edges';
import * as planParameters from './planParameters';
import {
  types
} from './generators/shapes';
import {
  convertSqMeterToSqFeet,
  convertSqMeterPriceToSqFeetPrice
} from './utils/convertMeasurement';
const ceilAmount = (meter) => Math.ceil(meter * 100) / 100;
const mapCeil = (item) => ({ ...item,
  amount: ceilAmount(item.amount)
});

const getFrontEdgeLength = (sinkModel, frontEdgeLength, fasteningType, availableFastenings) => {
  if (sinkModel && frontEdgeLength && availableFastenings) {
    if (availableFastenings.size && typeof frontEdgeLength !== 'number' && typeof frontEdgeLength !== 'string') { //  availableFastenings is a list of fastenings
      let frontEdgeLengthWhileFasteningisList;
      frontEdgeLengthWhileFasteningisList = frontEdgeLength.filter((lengthToRemove) => {
        if (typeof lengthToRemove === 'string' || lengthToRemove instanceof String || typeof lengthToRemove === 'number') {
          return lengthToRemove;
        }
      });
      if (frontEdgeLengthWhileFasteningisList.size === 0) {
        frontEdgeLength.filter((lengthToRemove) => {
          if (lengthToRemove.get(fasteningType)) {
            frontEdgeLengthWhileFasteningisList = lengthToRemove.get(fasteningType);
          }
        });
      }
      return frontEdgeLengthWhileFasteningisList;
    } else {
      return frontEdgeLength;
    }
  } else if (frontEdgeLength) {
    return frontEdgeLength;
  }
};

export const worktopEdgeItemsSelector = createSelector(
  materialParameterSelector,
  selectedEdgeSelector,
  plannedShapeParameterSelector,
  wallLocationParameterSelector,
  worktopWithOverhangMeasurementsSelector,
  overhangParameterSelector,
  overhangSideParameterSelector,
  freestandingHobsSelector,
  configurationSelector,
  selectedThicknessSelector,
  sinkModelParameterSelector,
  sinkFasteningParameterSelector,
  (materialParameter, edge, plannedShape, selectedWallLocation, getWorktopWithOverhangMeasurements, overhangParameter, overhangSide, freestandingHob, configuration, selectedThickness, sinkModel, sinkFastening) => {
    const materialSelected = materialParameter.getIn(['value', 'id']) !== NO_WORKTOP_MATERIAL;
    if (!edge || !plannedShape.get('value') || !selectedWallLocation.get('value') || !materialSelected) return List();
    const items = [];
    let plannedShapeToUse;
    const overhangId = overhangParameter.getIn(['value', 'id']);
    const overhangSideDimensions = overhangSide.getIn(['value', 'dimensions']);

    if (overhangId === NO_OVERHANG_ID) {
      plannedShapeToUse = plannedShape;
    } else {
      plannedShapeToUse = plannedShape.set('measurements', getWorktopWithOverhangMeasurements(overhangSide.get('value')));
    }

    const walls = selectedWallLocation.getIn(['value', 'wallLocations']).toJS();

    if (selectedThickness.get('priceByArea') && configuration.get('useDeCentralizedDoubleSidedCalculation')) {
      let nmbrWalls = 0;
      let hasWall = false;
      if (walls && overhangId === WATERFALL_ID_45_DEGREES || overhangId === WATERFALL_WITH_OVERHANG_ID_45_DEGREES || overhangId === WATERFALL_ID_90_DEGREES || overhangId === WATERFALL_WITH_OVERHANG_ID_90_DEGREES) {
        for (var i = 0, len = walls.length; i < len; i++) {
          if (walls[i] == "a") {
            hasWall = true;
          }
          if (walls[i] == "b") {
            nmbrWalls++;
          }
        }
        switch (nmbrWalls) {
          case 0:
            if (hasWall !== undefined && hasWall && (overhangId !== WATERFALL_WITH_OVERHANG_ID_45_DEGREES || overhangId !== WATERFALL_WITH_OVERHANG_ID_90_DEGREES)) {
              items.push({
                item: selectedThickness.getIn(['depths', 0, 'item']),
                amount: 0.02,
                sortOrder: 1,
                priceByArea: true,
              });
            }
            break;
          case 1:
            items.push({
              item: selectedThickness.getIn(['depths', 0, 'item']),
              amount: 0.01,
              sortOrder: 1,
              priceByArea: true,
            });
            break;
          default:
            break;
        }
      }
    }

    if (edge.get('frontEdge') && (edge.get('frontEdgeIncludedInPrice') === undefined || edge.get('frontEdgeIncludedInPrice') !== true)) {
      let frontEdgeLength = calculateFrontEdgeLength(plannedShapeToUse);
      if (overhangId === WATERFALL_ID_45_DEGREES || overhangId === WATERFALL_WITH_OVERHANG_ID_45_DEGREES || overhangId === WATERFALL_ID_90_DEGREES || overhangId === WATERFALL_WITH_OVERHANG_ID_90_DEGREES) {
        frontEdgeLength += calculateWaterfallEdgeLength(configuration.get('waterfallHeight'), walls, overhangSideDimensions);
      } else if (overhangId === ARC_ID) {
        frontEdgeLength -= calculateArcOverhangFrontEdgeLengthToRemove(plannedShape, walls, overhangSideDimensions);
      } 
      const frontEdgeToRemovePerHob = (freestandingHob.get('width') || configuration.get('freestandingHobDefaultWidth'));
      const frontEdgeToRemoveForHobs = frontEdgeToRemovePerHob * freestandingHob.get('value');

      const fasteningType = sinkFastening.getIn(['value', 'id']);
      const availableFastenings = sinkModel.getIn(['value', 'configuration', 'fastening']);
      const sinkLengthToRemove = getFrontEdgeLength(sinkModel, sinkModel.getIn(['value', 'configuration', 'frontEdgeLengthRemoved']), fasteningType, availableFastenings);
      const edgeamount = (frontEdgeLength - frontEdgeToRemoveForHobs);

      if (edgeamount && sinkLengthToRemove && (edgeamount > 0)) {
        if (edgeamount > sinkLengthToRemove) {
          items.push({
            amount: ((frontEdgeLength - frontEdgeToRemoveForHobs) - sinkLengthToRemove) / 1000,
            item: edge.get('frontEdge'),
            sortOrder: 2
          });
        }
      } else {
        items.push({
          amount: (frontEdgeLength - frontEdgeToRemoveForHobs) / 1000,
          item: edge.get('frontEdge'),
          sortOrder: 2
        });
      }
    }

    if (edge.get('visibleEdge')) {
      const visibleWorktopEdge = calculateVisibleEdgeLength(plannedShapeToUse, walls);

      let visibleOverhangEdge = 0;
      if (overhangId === WATERFALL_ID_45_DEGREES || overhangId === WATERFALL_WITH_OVERHANG_ID_45_DEGREES || overhangId === WATERFALL_ID_90_DEGREES || overhangId === WATERFALL_WITH_OVERHANG_ID_90_DEGREES) {
        visibleOverhangEdge = calculateWaterfallEdgeLength(configuration.get('waterfallHeight'), walls, overhangSideDimensions);
      } else if (overhangId === ARC_ID) {
        visibleOverhangEdge = calculateArcOverhangLength(plannedShapeToUse, walls, overhangSideDimensions);
      }

      const measurements = plannedShape.get('measurements');
      const dimensions = plannedShape.getIn(['value', 'dimensions']);
      const depth = measurements.filter((m, key) => {
        if (dimensions.getIn([key, 'type']) === types.DEPTH) {
          return true;
        }
        return false;
      }).sortBy().first();

      const edgeToAddForHobs = (depth * 2) * freestandingHob.get('value');
      items.push({
        amount: ((visibleWorktopEdge + visibleOverhangEdge + edgeToAddForHobs) / 1000),
        item: edge.get('visibleEdge'),
        sortOrder: 2
      });
    }
    return fromJS(items.map(mapCeil));
  }
);

export const splashbackItemsSelector = createSelector(
  splashbackParameterSelector,
  plannedShapeParameterSelector,
  wallLocationParameterSelector,
  materialParameterSelector,
  wallPanelExpressionParameterSelector,
  wallPanelMaxLengthSelector,
  wallPanelHeightInvalidSelector,
  wallEdgingStripCustomizableHeightInvalidSelector,
  wallPanelHeightSelector,
  wallEdgingStripHeightSelector,
  configurationSelector,
  wallEdgingStripMaterialParameterSelector,
  wallEdgingStripMaterialCustomizableParameterSelector,
  (splashback, plannedShape, wallLocation, materialParameter, wallPanelExpression, maxLength, wallPanelHeightInvalid, wallEdgingStripCustomizableInvalid, heightMeasurement, heightMeasurementCustomizableStrip, configuration, wallEdgingStripParameter, wallEdgingStripCustomizableParameter ) => {
    if (!configuration.get('noWallPanelMaterials')) {
      const materialSelected = materialParameter.getIn(['value', 'id']) !== NO_WORKTOP_MATERIAL;
      const wallLocations = wallLocation.getIn(['value', 'wallLocations']);
      const splashbackId = splashback.getIn(['value', 'id']);
    if ((!materialSelected && splashbackId === NONE) || splashbackId === NONE) {
      return List();
    }
    if (!wallLocations) {
      return fromJS([{
        error: 'missing wall locations for splashback'
      }]);
    }
    if(!materialSelected){
      if (splashbackId === WALL_EDGING_STRIP_ID) {
        return fromJS([{
          amount: ceilAmount(calculateWallEdgingStripLength(plannedShape, wallLocations) / 1000),
          item: wallEdgingStripParameter.getIn(['value', 'wallEdgingStrip'])
        }]);
      } else if (splashbackId === WALL_EDGING_STRIP_CUSTOMIZABLE_ID) {        
        return fromJS([{
          amount: ceilAmount(calculateWallEdgingStripLength(plannedShape, wallLocations) / 1000),
          item: wallEdgingStripCustomizableParameter.getIn(['value', 'wallEdgingStripCustomizable'])
        }]);
      }
    }

    if (splashbackId === WALL_EDGING_STRIP_ID) {
      return fromJS([{
        amount: ceilAmount(calculateWallEdgingStripLength(plannedShape, wallLocations) / 1000),
        item: materialParameter.getIn(['value', 'wallEdgingStrip'])
      }]);
    } else if (splashbackId === WALL_EDGING_STRIP_CUSTOMIZABLE_ID) {
      if (wallEdgingStripCustomizableInvalid) {
        return fromJS([{
          error: 'measurement invalid'
        }]);
      }
      return fromJS([{
        amount: ceilAmount(calculateWallEdgingStripLength(plannedShape, wallLocations) / 1000),
        item: materialParameter.getIn(['value', 'wallEdgingStripCustomizable'])
      }]);
    } else if (splashbackId === WALL_PANEL_ID) {
      if (wallPanelHeightInvalid) {
        return fromJS([{
          error: 'measurement invalid'
        }]);
      }

      const expression = wallPanelExpression.get('value');
      let wallPanelLength = null;
      if (expression !== undefined && expression !== null) {
        wallPanelLength = calculateWallPanelLength(plannedShape, wallLocations, expression.get('thickness'));
      }

      if (expression !== undefined && expression !== null && expression.get('priceByArea') && wallPanelLength !== null) {
        return fromJS([{
          item: expression.getIn(['heights', 0, 'item']),
          amount: ceilAmount((wallPanelLength / 1000) * (heightMeasurement / 1000)),
          sortOrder: 1,
          priceByArea: true,
        }]);
      } else {
        if (expression !== undefined && expression !== null) {
          return fromJS([{
            item: expression.get('heights').find((height) => heightMeasurement <= height.get('maxHeight')).get('item'),
            amount: ceilAmount(wallPanelLength / 1000),
            sortOrder: 1,
          }]);
        }
      }
    } else {
      return List();
    }
  }
  else {
    return [];
  }
}
);

export const kitchenIslandSplashbackItemsSelector = createSelector(
  splashbackParameterSelector,
  kitchenIslandWithMeasurementsSelector,
  kitchenIslandWallLocationParameterSelector,
  materialParameterSelector,
  kitchenIslandMaterialParameterSelector,
  wallPanelExpressionParameterSelector,
  wallPanelMaxLengthSelector,
  wallPanelHeightInvalidSelector,
  wallPanelHeightSelector,
  configurationSelector,
  kitchenIslandSplashbackSelector,
  wallEdgingStripMaterialParameterSelector,
  wallEdgingStripMaterialCustomizableParameterSelector,
  (splashback, plannedShape, wallLocation, materialParameter, kitchenIslandMaterialParameter, wallPanelExpression, maxLength, wallPanelHeightInvalid, heightMeasurement, configuration, kitchenIslandSplashback, wallEdgingStripParameter, wallEdgingStripCustomizableParameter) => {
    if (!configuration.get('noWallPanelMaterials')) {
      const materialSelected = materialParameter.getIn(['value', 'id']) !== NO_WORKTOP_MATERIAL;
      const kitchenIslandMaterial = kitchenIslandMaterialParameter.getIn(['value', 'id']);
      const normalWorktopMaterial = materialParameter.getIn(['value', 'id']);
      const splashbackId = splashback.getIn(['value', 'id']);
      const selectedWallEdgingStripMaterial = wallEdgingStripParameter.getIn(['value','id']);
      const selectedWallEdgingStripCustomizableMaterial = wallEdgingStripCustomizableParameter.getIn(['value','id']);
      if ((!materialSelected && splashbackId === NONE) || splashbackId === NONE || splashbackId === undefined || !kitchenIslandSplashback) {
        return List();
      }

      const wallLocations = wallLocation.getIn(['value', 'wallLocations']);
      if (!wallLocations) {
        return fromJS([{
          error: 'missing wall locations for splashback'
        }]);
      }

      if(normalWorktopMaterial === NO_WORKTOP_MATERIAL && splashbackId !== WALL_PANEL_ID){
        if (splashbackId === WALL_EDGING_STRIP_ID && kitchenIslandMaterial === selectedWallEdgingStripMaterial) {
          return fromJS([{
            amount: ceilAmount(calculateWallEdgingStripLength(plannedShape, wallLocations, kitchenIslandSplashback) / 1000),
            item: wallEdgingStripParameter.getIn(['value', 'wallEdgingStrip'])
          }]);
        } else if (splashbackId === WALL_EDGING_STRIP_CUSTOMIZABLE_ID && kitchenIslandMaterial === selectedWallEdgingStripCustomizableMaterial) {          
          return fromJS([{
            amount: ceilAmount(calculateWallEdgingStripLength(plannedShape, wallLocations, kitchenIslandSplashback) / 1000),
            item: wallEdgingStripCustomizableParameter.getIn(['value', 'wallEdgingStripCustomizable'])
          }]);
        } else {
          return List();
        }
      }
      if (splashbackId === WALL_EDGING_STRIP_ID) {
        if(kitchenIslandMaterial === normalWorktopMaterial){
          return fromJS([{
            amount: ceilAmount(calculateWallEdgingStripLength(plannedShape, wallLocations, kitchenIslandSplashback) / 1000),
            item: materialParameter.getIn(['value', 'wallEdgingStrip'])
          }]);
        } else {
          return List();
        }

      } else if (splashbackId === WALL_EDGING_STRIP_CUSTOMIZABLE_ID) {
        if(kitchenIslandMaterial === normalWorktopMaterial){
          return fromJS([{
            amount: ceilAmount(calculateWallEdgingStripLength(plannedShape, wallLocations, kitchenIslandSplashback) / 1000),
            item: materialParameter.getIn(['value', 'wallEdgingStripCustomizable'])
          }]);
        } else {
          return List();
        }
      } else if (splashbackId === WALL_PANEL_ID) {
        if (wallPanelHeightInvalid) {
          return fromJS([{
            error: 'measurement invalid'
          }]);
        }

        const expression = wallPanelExpression.get('value');
        let wallPanelLength = null;
        if (expression !== undefined && expression !== null) {
          wallPanelLength = calculateWallPanelLength(plannedShape, wallLocations, expression.get('thickness'), kitchenIslandSplashback);
        }
        if (expression !== undefined && expression !== null && expression.get('priceByArea') && wallPanelLength !== null) {
          return fromJS([{
            item: expression.getIn(['heights', 0, 'item']),
            amount: ceilAmount((wallPanelLength / 1000) * (heightMeasurement / 1000)),
            sortOrder: 1,
            priceByArea: true,
          }]);
        } else {
          if (expression !== undefined && expression !== null) {
            return fromJS([{
              item: expression.get('heights').find((height) => heightMeasurement <= height.get('maxHeight')).get('item'),
              amount: ceilAmount(wallPanelLength / 1000),
              sortOrder: 1,
            }]);
          }
        }
      } else {
        return List();
      }
    }
    else {
      return [];
    }
  }
);

function repeatItems(count, itemList, sortOrder) {
  if (count && count > 0 && itemList) {
    return itemList.map((item) => Map({
      amount: count,
      item,
      sortOrder,
    }));
  } else {
    return List();
  }
}

function repeatItemsOnlyFirst(count, itemList, sortOrder) {
  if (count && count > 0 && itemList) {
    return itemList.filter((item, index) => index === 0).map((item) => Map({
      amount: count,
      item,
      sortOrder,
    }));
  } else {
    return List();
  }
}

const createOperationSelector = (materialSelector, operationSelector, operationName) => createSelector(
  materialSelector,
  operationSelector,
  (selectedMaterial, operation) => repeat(operation, selectedMaterial, operationName)
);

function repeat(operation, selectedMaterial, operationName) {
  try {
    if (selectedMaterial !== null && selectedMaterial !== undefined && operation !== null && operation !== undefined &&
      operation.get('value') !== null && operation.get('value') !== undefined &&
      selectedMaterial.getIn(['value', 'operations', operationName]) !== null && selectedMaterial.getIn(['value', 'operations', operationName]) !== undefined) {
      return repeatItems(operation.get('value'), selectedMaterial.getIn(['value', 'operations', operationName]));
    } else {
      return List();
    }
  } catch (error) {
    console.log('Error creating operation selector ' + error)
  }
}

export const cutoutItemsSelector = createOperationSelector(materialParameterSelector, cutoutsSelector, planParameters.cutout);
export const singleCutItemsSelector = createOperationSelector(materialParameterSelector, singleCutsSelector, planParameters.singleCut);
export const doubleCutItemsSelector = createOperationSelector(materialParameterSelector, doubleCutsSelector, planParameters.doubleCut);
export const tripleCutItemsSelector = createOperationSelector(materialParameterSelector, tripleCutsSelector, planParameters.tripleCut);
export const highCabinetJointItemsSelector = createOperationSelector(materialParameterSelector, highCabinetJointsSelector, planParameters.highCabinetJoint);
export const roundedCornerItemsSelector = createOperationSelector(materialParameterSelector, roundedCornersSelector, planParameters.roundedCorner);
export const invertedCornerItemsSelector = createOperationSelector(materialParameterSelector, invertedCornersSelector, planParameters.invertedCorner);
export const tapHoleItemsSelector = createOperationSelector(materialParameterSelector, tapHolesSelector, planParameters.tapHole);
export const wallPanelRoundCutSmallItemsSelector = createOperationSelector(wallPanelMaterialParameterSelector, wallPanelRoundCutsSmallSelector, planParameters.wallPanelRoundCutSmall);
export const wallPanelRoundCutBigItemsSelector = createOperationSelector(wallPanelMaterialParameterSelector, wallPanelRoundCutsBigSelector, planParameters.wallPanelRoundCutBig);
export const wallPanelSquareCutItemsSelector = createOperationSelector(wallPanelMaterialParameterSelector, wallPanelSquareCutsSelector, planParameters.wallPanelSquareCut);
export const wallPanelSingleCutItemsSelector = createOperationSelector(wallPanelMaterialParameterSelector, wallPanelSingleCutsSelector, planParameters.wallPanelSingleCut);
export const wallPanelDoubleCutItemsSelector = createOperationSelector(wallPanelMaterialParameterSelector, wallPanelDoubleCutsSelector, planParameters.wallPanelDoubleCut);
export const wallPanelTripleCutItemsSelector = createOperationSelector(wallPanelMaterialParameterSelector, wallPanelTripleCutsSelector, planParameters.wallPanelTripleCut);
export const kitchenIslandSingleCutsItemsSelector = createOperationSelector(kitchenIslandMaterialParameterSelector, kitchenIslandSingleCutsSelector, planParameters.singleCut);
export const kitchenIslandRoundedCornersItemsSelector = createOperationSelector(kitchenIslandMaterialParameterSelector, kitchenIslandRoundedCornersSelector, planParameters.roundedCorner);
export const kitchenIslandSquareCutsItemsSelector = createOperationSelector(kitchenIslandMaterialParameterSelector, kitchenIslandSquareCutsSelector, planParameters.cutout);

export const drainerGroovesItemsSelector = createSelector(
  materialParameterSelector,
  drainerGroovesParameterSelector,
  (selectedMaterial, drainerGrooves) => {
    if (drainerGrooves.getIn(['value', 'id']) === 'none') {
      return [];
    } else {
      return repeatItems(1, selectedMaterial.getIn(['value', 'operations', 'drainerGrooves']));
    }
  }
);

const getWorktopSections = (shapeOrMeasurements, maxDepth, selectedThickness, overhang, overhangSide, measurementsWithOverhang, calculateSections, configuration, doubleSided) => {
  const maxLength = selectedThickness.getIn(['depths', 0, 'length']);
  const overhangDimensions = overhangSide.getIn(['value', 'dimensions']);
  const overhangSideId = overhangSide.getIn(['value', 'id']);
  const overhangId = overhang.getIn(['value', 'id']);

  if (overhangId === NO_OVERHANG_ID) {
    if (configuration.get('useDeCentralizedDoubleSidedCalculation')) {
      return calculateSections(shapeOrMeasurements, 100000, 100000, doubleSided);
    } else {
      return calculateSections(shapeOrMeasurements, maxDepth, maxLength, doubleSided);
    }
  } else {
    if (configuration.get('useDeCentralizedDoubleSidedCalculation')) {
      return calculateSections(shapeOrMeasurements, 100000, 100000, overhangDimensions, doubleSided, overhangSideId);
    } else {
      return calculateSections(shapeOrMeasurements, maxDepth, maxLength, overhangDimensions, doubleSided, overhangSideId);
    }
  }
};

const getWaterfallSections = (shape, maxDepth, overhang, overhangSide, configuration) => {
  const overhangId = overhang.getIn(['value', 'id']);
  const overhangDimensions = overhangSide.getIn(['value', 'dimensions']);

  if (overhangId === WATERFALL_ID_45_DEGREES || overhangId === WATERFALL_ID_90_DEGREES || overhangId === WATERFALL_WITH_OVERHANG_ID_45_DEGREES || overhangId === WATERFALL_WITH_OVERHANG_ID_90_DEGREES && !configuration.get('useDeCentralizedDoubleSidedCalculation')) {
    return calculateWaterfallSections(shape, maxDepth, configuration.get('waterfallHeight'), overhangDimensions);
  } else {
    return {
      sections: [],
      joints: 0,
    };
  }
};

const getOverhangOperationItems = (overhang, selectedMaterial) => {
  const overhangOperations = overhang.getIn(['value', 'operations']);

  if (overhangOperations) {
    return overhangOperations.flatMap((operation) => repeatItems(1, selectedMaterial.getIn(['value', 'operations', operation])));
  } else {
    return List();
  }
};

export const worktopItemsSelector = createSelector(
  materialParameterSelector,
  plannedShapeParameterSelector,
  maxDepthSelector,
  selectedThicknessSelector,
  selectedCoverPanelThicknessSelector,
  shapeInvalidSelector,
  sinkModelParameterSelector,
  overhangParameterSelector,
  worktopWithOverhangMeasurementsSelector,
  overhangSideParameterSelector,
  freestandingHobsSelector,
  configurationSelector,
  doubleSidedSelector,
  (selectedMaterial, plannedShape, maxDepth, selectedThickness, selectedCoverPanelThicknesses, invalid, sinkModel, overhang, measurementsWithOverhang, overhangSide, freestandingHob, configuration, doubleSided) => {

    const materialSelected = selectedMaterial.getIn(['value', 'id']) !== NO_WORKTOP_MATERIAL;
    if (!materialSelected) {
      return List();
    }
    let selectedCoverPanelThickness = null;
    if(configuration.get('useCoverPanels')){
      selectedCoverPanelThickness = selectedCoverPanelThicknesses;
    }
    if (invalid) {
      return fromJS([{
        error: 'measurement invalid'
      }]);
    }

    try {
      const shapeToUse = overhang.getIn(['value', 'id']) === NO_OVERHANG_ID ? plannedShape : plannedShape.set('measurements', measurementsWithOverhang(overhangSide.get('value')));

      const {
        sections: worktopSections,
        joints,
        singleCuts
      } = getWorktopSections(shapeToUse, maxDepth, selectedThickness, overhang, overhangSide, measurementsWithOverhang, calculateWorktopSections, configuration, doubleSided);
      const {
        sections: waterfallSections,
        joints: waterfallJoints
      } = getWaterfallSections(plannedShape, maxDepth, overhang, overhangSide, configuration);
      const overhangOperationItems = getOverhangOperationItems(overhang, selectedMaterial);

      let sections = worktopSections;

      const sinkLengthToRemove = sinkModel && sinkModel.getIn(['value', 'configuration', 'worktopLengthRemoved']);
      const depth = sinkModel && sinkModel.getIn(['value', 'configuration', 'depthCondition']);
      if (depth && sinkLengthToRemove) {
        if (depth.depth) {
          sections = sections.concat({
            length: -sinkLengthToRemove,
            depth: depth.depth
          });
        } else {
          throw new Error('Not sure how to perform sink length removal for this sink');
        }
      } else if (!depth && sinkLengthToRemove) {
        throw new Error('Unable to remove sink length without depth condition');
      }
      const freestandingHobValue = freestandingHob.get('value');
      const freestandingHobLengthToRemove = freestandingHobValue > 0 && (freestandingHob.get('width') || configuration.get('freestandingHobDefaultWidth'));
      if (freestandingHobLengthToRemove) {
        const sortedSections = _.orderBy(sections, 'depth');
        sections = sortedSections.concat({
          length: -freestandingHobLengthToRemove * freestandingHobValue,
          depth: sortedSections[0].depth
        });
      }
      const addSurfOperations = selectedMaterial.getIn(['value', 'operations', 'addSurface']);
      const sectionsWithItems = getSectionsWithItems(sections, selectedThickness, selectedCoverPanelThickness, addSurfOperations, configuration, false, false, waterfallSections.length);
      const waterfallSectionsWithItems = getSectionsWithItems(waterfallSections, selectedThickness, selectedCoverPanelThickness, addSurfOperations, configuration, true, false, null);
      let waterfallJointOperation = selectedMaterial.getIn(['value', 'operations', 'waterfallJoint45Degrees']);
      const selectedOverhang = overhang.getIn(['value', 'id']);
      if (selectedOverhang === WATERFALL_ID_90_DEGREES || selectedOverhang === WATERFALL_WITH_OVERHANG_ID_90_DEGREES) {
        waterfallJointOperation = selectedMaterial.getIn(['value', 'operations', 'waterfallJoint90Degrees']);
      }

      return fromJS(sectionsWithItems.map(mapCeil))
        .concat(fromJS(waterfallSectionsWithItems.map(mapCeil)))
        .concat(repeatItems(joints, selectedMaterial.getIn(['value', 'operations', 'joint']), 3))
        .concat(repeatItems(singleCuts, selectedMaterial.getIn(['value', 'operations', 'singleCut']), 4))
        .concat(repeatItemsOnlyFirst(waterfallJoints, waterfallJointOperation, 5))
        .concat(overhangOperationItems);
    } catch (e) {
      return fromJS([{
        error: e.message
      }]);
    }
  }
);

export const sinkModelItemSelector = createSelector(
  sinkSelector,
  (sink) => {
    if (sink) {
      return sink.get('item');
    }

    return null;
  }
);

const getSinkOperations = (sinkOperations, fasteningType, availableFastenings) => {
  if (sinkOperations) {
    if (availableFastenings) {
      if (availableFastenings.size) { // availableFastenings is a list of fastenings
        let sinkOperationsWhileFasteningisList;
        sinkOperationsWhileFasteningisList = sinkOperations.filter((fastening) => {
          if (typeof fastening === 'string' || fastening instanceof String) {
            return fastening;
          }
        });
        if (sinkOperationsWhileFasteningisList.size === 0){
          sinkOperations.filter((fastening) => {
            if (fastening.get(fasteningType)) {
              sinkOperationsWhileFasteningisList = fastening.get(fasteningType);
            }
          });
        }
        return sinkOperationsWhileFasteningisList;
      } else {
        return sinkOperations;
      }
    } else if (sinkOperations){
      return sinkOperations;
    }
  }
};

export const sinkItemsSelector = createSelector(
  materialParameterSelector,
  sinkMaterialParameterSelector,
  sinkFasteningParameterSelector,
  sinkModelItemSelector,
  sinkModelParameterSelector,
  configurationSelector,
  selectedThicknessSelector,
  (worktopMaterial, sinkMaterial, sinkFastening, sinkModelItem, sinkModelParameter, configuration, selectedThickness) => {
    if (sinkMaterial.getIn(['value', 'id']) === 'noSink') {
      return [];
    }

    const fasteningType = sinkFastening.getIn(['value', 'id']);
    const selectedProductName = !!sinkMaterial.getIn(['value', 'models']) && sinkMaterial.getIn(['value', 'models']).find((model) => model.get('ProductName')).get('ProductName');

    if (sinkModelItem) {
      let result = List([Map({
        amount: 1,
        item: sinkModelItem,
        sortOrder: 1
      })]);
      const sinkOperations = sinkModelParameter.getIn(['value', 'configuration', 'operations']);
      if (fasteningType === fasteningTypes.topMounted && !sinkOperations) {
        result = result.concat(repeatItems(1, worktopMaterial.getIn(['value', 'operations', 'cutout']), 2));
      }

      if (fasteningType === fasteningTypes.underGlued && configuration.get('underGluedByOperation')) {
        result = result.concat(repeatItems(1, worktopMaterial.getIn(['value', 'operations', fasteningTypes.underGlued]), 3))
          .concat(repeatItems(1, worktopMaterial.getIn(['value', 'operations', 'polishedCutout']), 4));
      }

      if (
        (!!worktopMaterial && !!worktopMaterial.getIn(['value', 'operations', 'clip'])) &&
        (!!fasteningTypes && fasteningTypes.topMounted && fasteningType === fasteningTypes.topMounted) &&
        (!!selectedThickness && selectedThickness.get('id') && selectedThickness.get('id') === '20') &&
        (!!selectedProductName && selectedProductName !== 'KILSVIKEN')
      ) {
        result = result.concat(repeatItems(1, worktopMaterial.getIn(['value', 'operations', 'clip']), 5));
      }

      const availableFastenings = sinkModelParameter.getIn(['value', 'configuration', 'fastening']);
      const sinkOps = getSinkOperations(sinkOperations, fasteningType, availableFastenings);
      if (sinkOps) {
        if (sinkOps.size > 0) {
          result = result.concat(sinkOps.flatMap((operation) => repeatItems(1, worktopMaterial.getIn(['value', 'operations', operation]))));
        }
      }

      return result;
    } else {
      return fromJS([{
        error: 'invalid sink selection'
      }]);
    }
  }
);

export const sinkUIItemsSelector = createSelector(
  materialParameterSelector,
  sinkMaterialParameterSelector,
  sinkFasteningParameterSelector,
  sinkUISelector,
  configurationSelector,
  selectedThicknessSelector,
  (worktopMaterial, sinkMaterial, sinkFastening, sinkModelItems, configuration, selectedThickness) => {
    
    const itemsArray = sinkModelItems && sinkModelItems.map((map) => map && map.get('item'));
    const sinkModels = sinkMaterial.getIn(['value', 'models']);
      
    if (sinkMaterial.getIn(['value', 'id']) === 'noSink') {
      return [];
    }

    const sinkUIItems = [];
    itemsArray.map((sinkModelItem, index) => {
      const sinkModelParameter = sinkModels.getIn([index]);
      const fasteningType = sinkFastening.getIn(['value', 'id']);
      const selectedProductName = !!sinkMaterial.getIn(['value', 'models']) && sinkMaterial.getIn(['value', 'models']).find((model) => model.get('ProductName')).get('ProductName');
  
      if (sinkModelItem) {
        let result = List([Map({
          amount: 1,
          item: sinkModelItem,
          sortOrder: 1
        })]);
        const sinkOperations = sinkModelParameter.getIn(['configuration', 'operations']);
        if (fasteningType === fasteningTypes.topMounted && !sinkOperations) {
          result = result.concat(repeatItems(1, worktopMaterial.getIn(['value', 'operations', 'cutout']), 2));
        }
  
        if (fasteningType === fasteningTypes.underGlued && configuration.get('underGluedByOperation')) {
          result = result.concat(repeatItems(1, worktopMaterial.getIn(['value', 'operations', fasteningTypes.underGlued]), 3))
            .concat(repeatItems(1, worktopMaterial.getIn(['value', 'operations', 'polishedCutout']), 4));
        }
  
        if (
          (!!worktopMaterial && !!worktopMaterial.getIn(['value', 'operations', 'clip'])) &&
          (!!fasteningTypes && fasteningTypes.topMounted && fasteningType === fasteningTypes.topMounted) &&
          (!!selectedThickness && selectedThickness.get('id') && selectedThickness.get('id') === '20') &&
          (!!selectedProductName && selectedProductName !== 'KILSVIKEN')
        ) {
          result = result.concat(repeatItems(1, worktopMaterial.getIn(['value', 'operations', 'clip']), 5));
        }
  
        const availableFastenings = sinkModelParameter.getIn(['configuration', 'fastening']);
        const sinkOps = getSinkOperations(sinkOperations, fasteningType, availableFastenings);
        if (sinkOps) {
          if (sinkOps.size > 0) {
            result = result.concat(sinkOps.flatMap((operation) => repeatItems(1, worktopMaterial.getIn(['value', 'operations', operation]))));
          }
        }
        sinkUIItems.push(result);
      } else {
        sinkUIItems.push(null);
      }
    });
    return sinkUIItems;
  }
);


export const kitchenIslandWorktopItemsSelector = createSelector(
  kitchenIslandMaterialParameterSelector,
  kitchenIslandWithMeasurementsSelector,
  maxDepthSelector,
  selectedKitchenIslandThicknessSelector,
  selectedKitchenIslandCoverPanelThicknessSelector,
  kitchenIslandInvalidSelector,
  kitchenIslandOverhangParameterSelector,
  kitchenIslandWithOverhangMeasurementsSelector,
  kitchenIslandOverhangSideParameterSelector,
  configurationSelector,
  kitchenIslandDoubleSidedSelector,
  (selectedMaterial, kitchenIsland, maxDepth, selectedThickness, selectedCoverPanelThicknesses, invalid, overhang, measurementsWithOverhang, overhangSide, configuration, doubleSided) => {
    if (kitchenIsland.getIn(['value', 'id']) === NO_KITCHEN_ISLAND_ID) {
      return List();
    }
    let selectedCoverPanelThickness = null;
    if(configuration.get('useCoverPanels')){
      selectedCoverPanelThickness = selectedCoverPanelThicknesses;
    }
    if (invalid) {
      return fromJS([{
        error: 'kitchen island measurements invalid'
      }]);
    }
    try {
      const measurementsToUse = overhang.getIn(['value', 'id']) === NO_OVERHANG_ID ? kitchenIsland.get('measurements') : measurementsWithOverhang(overhangSide.get('value'));

      const {
        sections,
        joints
      } = getWorktopSections(measurementsToUse, maxDepth, selectedThickness, overhang, overhangSide, measurementsWithOverhang, calculateKitchenIslandSections, configuration, doubleSided);
      const {
        sections: waterfallSections,
        joints: waterfallJoints
      } = getWaterfallSections(kitchenIsland, maxDepth, overhang, overhangSide, configuration);
      const overhangOperationItems = getOverhangOperationItems(overhang, selectedMaterial);
      const addSurfOperations = selectedMaterial.getIn(['value', 'operations', 'addSurface']);
      const sectionsWithItems = getSectionsWithItems(sections, selectedThickness, selectedCoverPanelThickness, addSurfOperations, configuration, false, true, waterfallSections.length);

      console.log("sectionsWithItems is: ", sectionsWithItems);
      const waterfallSectionsWithItems = getSectionsWithItems(waterfallSections, selectedThickness, selectedCoverPanelThickness, addSurfOperations, configuration, true, true, null);
      let waterfallJointOperation = selectedMaterial.getIn(['value', 'operations', 'waterfallJoint45Degrees']);
      const selectedOverhang = overhang.getIn(['value', 'id']);
      if (selectedOverhang === WATERFALL_ID_90_DEGREES || selectedOverhang === WATERFALL_WITH_OVERHANG_ID_90_DEGREES) {
        waterfallJointOperation = selectedMaterial.getIn(['value', 'operations', 'waterfallJoint90Degrees']);
      }
      if (!configuration.get('useDeCentralizedDoubleSidedCalculation')) {
        return fromJS(sectionsWithItems.map(mapCeil))
          .concat(fromJS(waterfallSectionsWithItems.map(mapCeil)))
          .concat(repeatItems(joints, selectedMaterial.getIn(['value', 'operations', 'joint'])))
          .concat(repeatItemsOnlyFirst(waterfallJoints, waterfallJointOperation, 5))
          .concat(overhangOperationItems);
      } else {
        return fromJS(sectionsWithItems.map(mapCeil))
          .concat(fromJS(waterfallSectionsWithItems.map(mapCeil)))
          .concat(repeatItems(joints, selectedMaterial.getIn(['value', 'operations', 'joint'])))
          .concat(overhangOperationItems);
      }
    } catch (e) {
      return fromJS([{
        error: e.message
      }]);
    }
  }
);

function calculateNrOfSides(index, nrOfCoverPanelsLengthWise, isWaterfall, sections, indexOfWorktopSection) {
  const sectionCount = sections.length;
  const isFirstSection = indexOfWorktopSection === 0;
  const isLastSection = indexOfWorktopSection === (sectionCount - 1);
  const isBetweenSection = sectionCount > 2 && !isFirstSection && !isLastSection;
  if (isWaterfall || isBetweenSection) {
    return 0;
  }
  if (nrOfCoverPanelsLengthWise === 1) {
    return 40;
  }
  if (index === 0) {
    return (nrOfCoverPanelsLengthWise === 1 && !isFirstSection && !isLastSection) ? 40 : (nrOfCoverPanelsLengthWise > 1 ? 20 : 40);
  } else if (index === 1) {
    return (nrOfCoverPanelsLengthWise === 2 && isLastSection) ? 20 : (nrOfCoverPanelsLengthWise > 2 ? 0 : 40);
  } else if (index > 1) {
    return (index === (nrOfCoverPanelsLengthWise - 1) && isLastSection) ? 20 : (index < (nrOfCoverPanelsLengthWise - 1) ? 0 : 40);
  }
  return 40;
}

function getCoverPanelItems(coverPanelThickness, section, isWaterfall, sections, indexOfWorktopSection) {
  let coverPanelItems = [];
  let coverPanel2CMShorterOneEachSide = 40;
  const coverPanel2CMFrontSideMargin = 20;

  let depthOfSection = section.depth;
  let lengthOfSection = section.length;
  let nrOfDepthsToConsider = Math.ceil(section.depth / 635);
  const lengthLeftAfterFirstCalc = section.length - 2440;
  let nrOfLengthsToConsider = 1;
  if(lengthLeftAfterFirstCalc > 0){
    nrOfLengthsToConsider += Math.ceil(lengthLeftAfterFirstCalc / (2400));
  }

  let lengthIndex = 0;
  for (let i = 0; i < nrOfDepthsToConsider; i++) {
    let decreasedDepthOncePerLengthOfSection = false;
    lengthOfSection = section.length;
    const depthsList = coverPanelThickness.get('depths').sortBy((thicknessItem) => thicknessItem.getIn(['maxWidth', 0, 'measure']));
    let maxCount = 20; // using a count, to make sure that the while loop does not accidentaly continue forever
    let findCoverPanelbyLength;
    while((lengthOfSection > 0) && maxCount > 0){
      coverPanel2CMShorterOneEachSide = calculateNrOfSides(lengthIndex, nrOfLengthsToConsider, isWaterfall, sections, indexOfWorktopSection)
      lengthIndex++;
      maxCount--;
      const findCoverPanelbyWidth = depthsList.find((thicknessItem) => {
        const widthOfCoverPanel = thicknessItem.getIn(['maxWidth', 0, 'measure']) + coverPanel2CMFrontSideMargin;
        return depthOfSection <= widthOfCoverPanel;
      }) || depthsList.last(); // Fallback to the last item if no match is found
    
      const lengthList = findCoverPanelbyWidth.get('length');
      findCoverPanelbyLength = lengthList.find((lengthItem) => {
        const lengthOfCoverPanel = lengthItem.get('measure') + coverPanel2CMShorterOneEachSide;
        return lengthOfSection <= lengthOfCoverPanel;
      }) || lengthList.last(); // Fallback to the last item if no match is found
    
      const measurements = findCoverPanelbyLength.getIn(['items', 0, 'ItemMeasureList', 'ItemMeasure']);
    
      let depthVal = 0;
      let lengthVal = 0;
      
      measurements.find((msmt) => {
        if(msmt.get('ItemMeasureTypeNameEn') === 'Width'){
          depthVal = (msmt.get('ItemMeasureValue') + coverPanel2CMFrontSideMargin);
        } else if(msmt.get('ItemMeasureTypeNameEn') === 'Height'){
          lengthVal = (msmt.get('ItemMeasureValue') + coverPanel2CMShorterOneEachSide);
        }
      })
      lengthOfSection -= lengthVal;
      if(lengthOfSection <= 0 && depthOfSection >= 0){
        if(!decreasedDepthOncePerLengthOfSection){
          decreasedDepthOncePerLengthOfSection = true;
          depthOfSection -= depthVal;
        }
      } 
      coverPanelItems.push(
        {
          item: findCoverPanelbyLength.getIn(['items', 0]),
          amount: 1,
          sortOrder: 1,
          priceByArea: false,
        })
    }
  }
  return coverPanelItems;
}

function getSectionsWithItems(sections, selectedThickness, selectedCoverPanelThickness, addSurfOperations, configuration, isWaterfall, isKitchenIsland, nrOfWaterFalls) {
  if (selectedThickness.get('priceByArea')) {
    var sectionCalculation = 0;
    var waterfallCalculation = 0;
    var lengthOfDoubleSidedSidesPolishArea = 600;
    return sections.flatMap((section, index) => {
      const mySections = [];
      const calculatingCornerSection = section.isVshapeCorner || section.isCshapeCorner;
      const cornerRemovalAmount = section.cornerRemovalAmount;
      let itemAmount = (section.length * section.depth) / (10 ** 6);
      if (calculatingCornerSection) {
        itemAmount = ((section.length * section.depth) / (10 ** 6)) - cornerRemovalAmount;
      }
      const worktop = {
        item: selectedThickness.getIn(['depths', 0, 'item']),
        amount: itemAmount,
        sortOrder: 1,
        priceByArea: true,
      };
      mySections.push(worktop);
      if(configuration.get('useCoverPanels')){
        const coverPanel = getCoverPanelItems(selectedCoverPanelThickness, section, isWaterfall, sections, index);
        mySections.push(...coverPanel);
      }
      if (section.doubleSided && addSurfOperations && configuration.get('useDeCentralizedDoubleSidedCalculation')) {
        if (!isKitchenIsland && !isWaterfall && !section.doubleSided){
          return mySections;
        }

        // specifically for shapes with more then one worktop (II-shape), since the II-shape has two worktops, each needs to calculate its own number of waterfalls
        if (section.nrOfWaterfalls !== undefined && section.nrOfWaterfalls !== null && section.nrOfWaterfalls >= 0) {
          nrOfWaterFalls = section.nrOfWaterfalls;
        }
        if (!isWaterfall) {
          if (nrOfWaterFalls !== null && nrOfWaterFalls === 0) {
            lengthOfDoubleSidedSidesPolishArea = 600;
          } else if (nrOfWaterFalls !== null && nrOfWaterFalls === 1) {
            lengthOfDoubleSidedSidesPolishArea = 300;
          } else if (nrOfWaterFalls !== null && nrOfWaterFalls === 2) {
            lengthOfDoubleSidedSidesPolishArea = 0;
          }
        }
        if (calculatingCornerSection) {
          lengthOfDoubleSidedSidesPolishArea = 0;
          nrOfWaterFalls = 0;
          if (!isWaterfall && !isKitchenIsland && section.doubleSided) {
            // double sided for corner section is calculated using the frontside length, instead of the backside length.
            section.length = section.lengthFront;
          }
        }
        console.log("Section measurements, depth: " + section.depth + " length: " + section.length);

        if (section.depth >= 301 && section.length >= 601) {
          sectionCalculation = (((section.depth - 300) * lengthOfDoubleSidedSidesPolishArea) / (10 ** 6)) + ((section.length * 300) / (10 ** 6));
          waterfallCalculation = ((section.length * 300) / (10 ** 6));
        } else if (section.depth >= 301 && section.length <= 601) {
          if (lengthOfDoubleSidedSidesPolishArea > section.length) {
            lengthOfDoubleSidedSidesPolishArea = section.length;
          }
          sectionCalculation = (((section.depth - 300) * lengthOfDoubleSidedSidesPolishArea) / (10 ** 6)) + ((section.length * 300) / (10 ** 6))
          waterfallCalculation = ((section.length * section.depth) / (10 ** 6));
        } else if (section.depth <= 301 && section.length >= 601) {
          if (lengthOfDoubleSidedSidesPolishArea > section.length) {
            lengthOfDoubleSidedSidesPolishArea = section.length;
          }
          sectionCalculation = ((section.depth * lengthOfDoubleSidedSidesPolishArea) / (10 ** 6));
          waterfallCalculation = ((section.length * section.depth) / (10 ** 6));
        } else if (section.depth < 301 && section.length < 601) {
          sectionCalculation = ((section.length * section.depth) / (10 ** 6));
          waterfallCalculation = ((section.length * section.depth) / (10 ** 6));
        }

        console.log("Calculated section value:" + sectionCalculation + " (used if needed)");
        console.log("Calculated waterfall value:" + waterfallCalculation + " (used if needed)");

        if (calculatingCornerSection && section.doubleSided) {
          sectionCalculation = sectionCalculation + section.cornerAddDoubleSidedAmount;
        }

        const isDecentralizedWorktopWaterfall = (isWaterfall && !isKitchenIsland);
        if (isDecentralizedWorktopWaterfall) {
          const waterfallExtraPolishingAmount = 0.02;
          waterfallCalculation = Number((waterfallCalculation + waterfallExtraPolishingAmount).toFixed(5));
        }
        if (selectedThickness!==null && selectedThickness!==undefined && selectedThickness.get('thickness') < 40) {
          if (isWaterfall) {
            const doubleSided = {
              item: addSurfOperations.find((operationI) => {
                const operation = operationI.toJS();
                return true;
              }),
              amount: waterfallCalculation,
              sortOrder: 3,
            };
            console.log([worktop, doubleSided])
            mySections.push(doubleSided);
            return mySections;
          } else {
            const doubleSided = {
              item: addSurfOperations.find((operationI) => {
                const operation = operationI.toJS();
                return true;
              }),
              amount: sectionCalculation,
              sortOrder: 3,
            };
            console.log([worktop, doubleSided])
            mySections.push(doubleSided);
            return mySections;
          }
        } else {
          if (isWaterfall) {
            const operation = worktop.item.toJS();
            const doubleSided = {
              item: operation,
              amount: waterfallCalculation,
              sortOrder: 1,
            };
            console.log([worktop, doubleSided])
            mySections.push(doubleSided);
            return mySections;
          } else {
            const operation = worktop.item.toJS();
            const doubleSided = {
              item: operation,
              amount: sectionCalculation,
              sortOrder: 1,
            };
            mySections.push(doubleSided);
            return mySections;
          }
        }
      } else {
        return mySections;
      }
    }).filter((def) => def && def.item);
  } else {
    return sections.flatMap((section) => {
        let amount = section.length / 1000;
        const calculatingCornerSection = section.isVshapeCorner || section.isCshapeCorner;
        if (!selectedThickness.get('priceByArea') && calculatingCornerSection && (section.isCshapeCorner || section.isVshapeCorner)) {
          amount = Math.sqrt(((section.length * section.depth) * 2)) / 1000;
          if (section.isCshapeCorner) {
            section.depth = section.depthCentralized;
          }
        }
        const worktop = {
          item: selectedThickness.get('depths').sortBy((thicknessItem) => thicknessItem.get('maxDepth')).find((thicknessItem) => section.depth <= thicknessItem.get('maxDepth')).get('item'),
          amount,
          sortOrder: 1,
        };
        if (section.doubleSided && addSurfOperations) {
          if (configuration.get('useDeCentralizedDoubleSidedCalculation') && selectedThickness.get('priceByArea')) {
            if (!isKitchenIsland && !isWaterfall && !section.doubleSided){
              return [worktop];
            }

            console.log("Section measurements, depth: " + section.depth + " length: " + section.length);

            // specifically for shapes with more then one worktop (II-shape), since the II-shape has two worktops, each needs to calculate its own number of waterfalls
            if (section.nrOfWaterfalls) {
              nrOfWaterFalls = section.nrOfWaterfalls;
            }

            if (calculatingCornerSection) {
              lengthOfDoubleSidedSidesPolishArea = 0;
              nrOfWaterFalls = 0;
              if (!isWaterfall && !isKitchenIsland && section.doubleSided) {
                // double sided for corner section is calculated using the frontside length, instead of the backside length.
                section.length = section.lengthFront;
              }
            }

            if (section.depth >= 301 && section.length >= 601) {
              sectionCalculation = (((section.depth - 300) * lengthOfDoubleSidedSidesPolishArea) / (10 ** 6)) + ((section.length * 300) / (10 ** 6));
              waterfallCalculation = ((section.length * 300) / (10 ** 6));
            } else if (section.depth >= 301 && section.length <= 601) {
              if (lengthOfDoubleSidedSidesPolishArea > section.length) {
                lengthOfDoubleSidedSidesPolishArea = section.length;
              }
              sectionCalculation = (((section.depth - 300) * lengthOfDoubleSidedSidesPolishArea) / (10 ** 6)) + ((section.length * 300) / (10 ** 6))
              waterfallCalculation = ((section.length * section.depth) / (10 ** 6));
            } else if (section.depth <= 301 && section.length >= 601) {
              if (lengthOfDoubleSidedSidesPolishArea > section.length) {
                lengthOfDoubleSidedSidesPolishArea = section.length;
              }
              sectionCalculation = ((section.depth * lengthOfDoubleSidedSidesPolishArea) / (10 ** 6));
              waterfallCalculation = ((section.length * section.depth) / (10 ** 6));
            } else if (section.depth < 301 && section.length < 601) {
              sectionCalculation = ((section.length * section.depth) / (10 ** 6));
              waterfallCalculation = ((section.length * section.depth) / (10 ** 6));
            }

            console.log("Calculated section value:" + sectionCalculation + " (used if needed)");
            console.log("Calculated waterfall value:" + waterfallCalculation + " (used if needed)");

            if (calculatingCornerSection && section.doubleSided) {
              sectionCalculation = sectionCalculation + section.cornerAddDoubleSidedAmount;
            }
            const isDecentralizedWorktopWaterfall = (isWaterfall && !isKitchenIsland);
            if (isDecentralizedWorktopWaterfall) {
              const waterfallExtraPolishingAmount = 0.02;
              waterfallCalculation = Number((waterfallCalculation + waterfallExtraPolishingAmount).toFixed(5));
            }
            if (isWaterfall) {
              const doubleSided = {
                item: addSurfOperations.find((operationI) => {
                  const operation = operationI.toJS();
                  return true;
                }),
                amount: waterfallCalculation,
                sortOrder: 3,
              };
              console.log([worktop, doubleSided])
              return [worktop, doubleSided];
            } else {
              const doubleSided = {
                item: addSurfOperations.find((operationI) => {
                  const operation = operationI.toJS();
                  return true;
                }),
                amount: sectionCalculation,
                sortOrder: 3,
              };
              console.log([worktop, doubleSided])
              return [worktop, doubleSided];
            }
          } else {
            const doubleSided = {
              item: addSurfOperations.find((operationI) => {
                const operation = operationI.toJS();
                const maxDepth = getItemMeasurement(operation, 'Max. depth');
                const minDepth = getItemMeasurement(operation, 'Min. depth');
                return section.depth <= maxDepth && section.depth >= minDepth;
              }),
              amount,
              sortOrder: 3,
            };
            console.log([worktop, doubleSided])
            return [worktop, doubleSided];
          }
        } else {
          return [worktop];
        }
      })
      .filter((def) => def && def.item);
  }
}

export const kitchenIslandEdgeItemsSelector = createSelector(
  selectedKitchenIslandEdgeSelector,
  kitchenIslandParameterSelector,
  kitchenIslandWithOverhangMeasurementsSelector,
  kitchenIslandOverhangParameterSelector,
  kitchenIslandOverhangSideParameterSelector,
  kitchenIslandWallLocationParameterSelector,
  configurationSelector,
  (edge, kitchenIsland, kitchenIslandWithOverhangMeasurements, overhang, overhangSide, selectedWallLocation, configuration) => {
    if (!edge || !kitchenIsland.get('value') || kitchenIsland.getIn(['value', 'id']) === NO_KITCHEN_ISLAND_ID) return List();
    const items = [];
    const overhangId = overhang.getIn(['value', 'id']);
    const overhangSideDimensions = overhangSide.getIn(['value', 'dimensions']);
    const withOverhangMeasurements = kitchenIslandWithOverhangMeasurements(overhangSide.get('value'));

    const walls = selectedWallLocation.getIn(['value', 'wallLocations']) !== null ? selectedWallLocation.getIn(['value', 'wallLocations']).toJS() : [];

    let kitchenIslandToUse;
    if (overhangId === NO_OVERHANG_ID) {
      kitchenIslandToUse = kitchenIsland.set('measurements', withOverhangMeasurements);
    } else {
      kitchenIslandToUse = kitchenIsland.set('measurements', withOverhangMeasurements);
    }

    if (edge.get('frontEdge') && (edge.get('frontEdgeIncludedInPrice') === undefined || edge.get('frontEdgeIncludedInPrice') !== true)) {
      let frontEdgeLength = withOverhangMeasurements.get('a');

      if (overhangId === WATERFALL_ID_45_DEGREES || overhangId === WATERFALL_WITH_OVERHANG_ID_45_DEGREES || overhangId === WATERFALL_ID_90_DEGREES || overhangId === WATERFALL_WITH_OVERHANG_ID_90_DEGREES) {
        frontEdgeLength += calculateWaterfallEdgeLength(configuration.get('waterfallHeight'), [], overhangSideDimensions);
      } else if (overhangId === ARC_ID) {
        frontEdgeLength -= calculateArcOverhangFrontEdgeLengthToRemove(kitchenIslandToUse, [], overhangSideDimensions);
      }

      items.push({
        amount: frontEdgeLength / 1000,
        item: edge.get('frontEdge'),
        sortOrder: 2
      });
    }

    if (edge.get('visibleEdge')) {
      let visibleEdgeLength = calculateVisibleEdgeLength(kitchenIslandToUse, walls);
      if (overhangId === WATERFALL_ID_45_DEGREES || overhangId === WATERFALL_WITH_OVERHANG_ID_45_DEGREES || overhangId === WATERFALL_ID_90_DEGREES || overhangId === WATERFALL_WITH_OVERHANG_ID_90_DEGREES) {
        visibleEdgeLength += calculateWaterfallEdgeLength(configuration.get('waterfallHeight'), [], overhangSideDimensions);
      } else if (overhangId === ARC_ID) {
        visibleEdgeLength += calculateArcOverhangLength(kitchenIslandToUse, [], overhangSideDimensions);
      }

      items.push({
        amount: visibleEdgeLength / 1000,
        item: edge.get('visibleEdge'),
        sortOrder: 2
      });
    }
    return fromJS(items.map(mapCeil));
  }
);

export const itemListSelector = createSelector(
  worktopItemsSelector,
  cutoutItemsSelector,
  singleCutItemsSelector,
  doubleCutItemsSelector,
  tripleCutItemsSelector,
  highCabinetJointItemsSelector,
  roundedCornerItemsSelector,
  invertedCornerItemsSelector,
  worktopEdgeItemsSelector,
  sinkItemsSelector,
  tapHoleItemsSelector,
  drainerGroovesItemsSelector,
  splashbackItemsSelector,
  kitchenIslandSplashbackItemsSelector,
  wallPanelRoundCutSmallItemsSelector,
  wallPanelRoundCutBigItemsSelector,
  wallPanelSquareCutItemsSelector,
  wallPanelSingleCutItemsSelector,
  wallPanelDoubleCutItemsSelector,
  wallPanelTripleCutItemsSelector,
  kitchenIslandWorktopItemsSelector,
  kitchenIslandEdgeItemsSelector,
  kitchenIslandSingleCutsItemsSelector,
  kitchenIslandRoundedCornersItemsSelector,
  kitchenIslandSquareCutsItemsSelector,
  // (...itemLists) => itemLists.reduce((result, itemList) => result.concat(itemList)),
  (...itemLists) => {
    return itemLists.reduce((result, itemList) => {
      if(itemList && itemList.size > 0){
        return result.concat(itemList);
      } else {
        return result;
      }
    });
  });

export const makeWorktopSummarySelector = (selectors, testId, summaryTitle, onRemove) => {
  return createSelector(
    selectors,
    (worktopItems, edgeItems, selectedExpression, overhang) => {
      if (worktopItems.size <= 0) {
        return [];
      }
      const items = worktopItems.concat(edgeItems);
      console.log("items is: ", items);
      const item = worktopItems.getIn([0, 'item']);
      if(!item){
        return [];
      }
      const subtitle = item.get('ValidDesignText');
      const overhangId = overhang.getIn(['value', 'id']);

      let title = summaryTitle;

      if (overhangId !== NO_OVERHANG_ID) {
        const overhangSuffix = overhang.getIn(['value', 'displayName']).toJS();
        if (overhangId === WATERFALL_ID_45_DEGREES || overhangId === WATERFALL_WITH_OVERHANG_ID_45_DEGREES || overhangId === WATERFALL_ID_90_DEGREES || overhangId === WATERFALL_WITH_OVERHANG_ID_90_DEGREES) {
          title = [summaryTitle, ' (', itemListMessages.with, ' ', overhangSuffix, ')'];
        } else {
          title = [summaryTitle, ' (', itemListMessages.with, ' ', overhangSuffix, ' ', itemListMessages.overhang, ')'];
        }
      }

      const size = '';
      const priceIsEstimate = [ROUNDED_OVERHANG_ID, HALF_ROUNDED_OVERHANG_ID, EMERALD_ID, HALF_EMERALD_ID].some((id) => id === overhangId);

      return [{
        title,
        subtitle,
        size,
        scaleImage: true,
        onRemove,
        priceIsEstimate,
        image: selectedExpression.getIn(['value', 'displayImage']),
        price: calculatePrice(items),
        dualPrice: calculateDualPrice(items),
        familyPrice: calculatePriceInclFamilyPrice(items),
        dualFamilyPrice: calculateDualPriceInclFamilyPrice(items),
        items: items.toJS(),
        testId,
      }];
    }
  );
};

export const makeSplasbackSummarySelector = (selectors, testId, summaryTitle, onRemove) => {
  return createSelector(
    selectors,
    (splashbackItems, kitchenIslandSplashbackItems, materialParameter, selectedSplashback, wallPanelExpression, configuration) => {
      if(!splashbackItems){
        return [];
      }
      const splashbackId = selectedSplashback.getIn(['value', 'id']);
      if (splashbackId === NONE || (configuration !== undefined && configuration !== null && configuration.get('noWallPanelMaterials'))) {
        const materialSelected = materialParameter.getIn(['value', 'id']) !== NO_WORKTOP_MATERIAL;
        if (splashbackId === NONE || (splashbackId === NONE && !materialSelected) || (configuration !== undefined && configuration !== null && configuration.get('noWallPanelMaterials'))) {
          return [];
        }
      }
      let totalSplashbackItems = splashbackItems;
      if (kitchenIslandSplashbackItems && kitchenIslandSplashbackItems.size > 0 && kitchenIslandSplashbackItems.getIn([0, 'amount']) > 0) {
        const kitchenIslandWithSplashbackItems = kitchenIslandSplashbackItems.setIn([0, 'item', 'isKitchenIslandSplashback'], true);
        totalSplashbackItems = splashbackItems.concat(kitchenIslandWithSplashbackItems);
      }
      const item = splashbackItems.getIn([0, 'item']);
      if(!item){
        return [];
      }
      const subtitle = `${item.get('ProductTypeName')}, ${item.get('ValidDesignText')}`;

      let image;
      if (splashbackId === WALL_PANEL_ID) {
        image = wallPanelExpression.getIn(['value', 'displayImage']);
      } else if (splashbackId === WALL_EDGING_STRIP_ID || splashbackId === WALL_EDGING_STRIP_CUSTOMIZABLE_ID) {
        image = selectedSplashback.getIn(['value', 'pictogram']);
      }
      const size = '';
      return [{
        title: parameterMessages.splashback,
        subtitle,
        image,
        size,
        onRemove: () => selectSplashback(NONE),
        price: calculatePrice(totalSplashbackItems),
        dualPrice: calculateDualPrice(totalSplashbackItems),
        items: totalSplashbackItems.toJS(),
        testId: planParameters.splashback,
        splitEachItemInSummary: true,
      }];
    }
  );
};

export const worktopSummarySelector = makeWorktopSummarySelector(
  [
    worktopItemsSelector,
    worktopEdgeItemsSelector,
    expressionParameterSelector,
    overhangParameterSelector,
  ],
  'worktop',
  itemListMessages.worktop,
);

export const kitchenIslandSummarySelector = makeWorktopSummarySelector(
  [
    kitchenIslandWorktopItemsSelector,
    kitchenIslandEdgeItemsSelector,
    kitchenIslandExpressionParameterSelector,
    kitchenIslandOverhangParameterSelector,
  ],
  'kitchen-island',
  parameterMessages.kitchenIsland,
  () => selectKitchenIsland(NO_KITCHEN_ISLAND_ID),
);

export const sinkSummarySelector = createSelector(
  sinkItemsSelector,
  sinkMaterialParameterSelector,
  sinkModelParameterSelector,
  sinkModelItemSelector,
  sinkFasteningParameterSelector,
  (sinkItems, selectedSinkMaterial, selectedSinkModel, sinkModelItem, sinkFastening) => {
    if (selectedSinkMaterial.getIn(['value', 'id']) === 'noSink' || !sinkModelItem) {
      return [];
    }
    const title = sinkFastening.getIn(['value', 'summaryTitle']).toJS();
    const subtitle = `${sinkModelItem.get('ProductTypeName')}, ${sinkModelItem.get('ValidDesignText')}`;
    const size = sinkModelItem.get('ItemMeasureReferenceTextMetric');
    const imperialSize = sinkModelItem.get('ItemMeasureReferenceTextImperial');
    return [{
      title,
      subtitle,
      image: selectedSinkModel.getIn(['value', 'displayImage']),
      size,
      imperialSize,
      onRemove: () => selectSinkMaterial('noSink'),
      price: calculatePrice(sinkItems),
      dualPrice: calculateDualPrice(sinkItems),
      items: sinkItems.toJS(),
      testId: 'sink',
    }];
  }
);

export const splashbackSummarySelector = makeSplasbackSummarySelector(
  [
    splashbackItemsSelector,
    kitchenIslandSplashbackItemsSelector,
    materialParameterSelector,
    splashbackParameterSelector,
    wallPanelExpressionParameterSelector,
    configurationSelector,
  ],
  'worktop',
  itemListMessages.worktop,
);

export const cutoutsSummarySelector = createSelector(
  cutoutItemsSelector,
  (cutoutItems) => {
    if (cutoutItems.size === 0) return [];
    return [{
      title: parameterMessages.cutoutsForHob,
      image: PictoCutoutsHob,
      padImage: true,
      onRemove: resetCutouts,
      price: calculatePrice(cutoutItems),
      dualPrice: calculateDualPrice(cutoutItems),
      items: cutoutItems.toJS(),
      testId: planParameters.cutout,
    }];
  }
);

export const freestandingHobsSummarySelector = createSelector(
  freestandingHobsSelector,
  (freestandingHobs) => {
    const amount = freestandingHobs.get('value');
    if (freestandingHobs && amount === 0) return [];
    return [{
      title: parameterMessages.freestandingHob,
      image: PictoFreestandingHob,
      padImage: true,
      onRemove: resetFreestandingHobs,
      testId: planParameters.freestandingHob,
      items: null,
      amount,
    }];
  }
);

export const singleCutsSummarySelector = createSelector(
  singleCutItemsSelector,
  (singleCutItems) => {
    if (singleCutItems.size === 0) return [];
    return [{
      title: parameterMessages.singleCuts,
      image: PictoSingleCuts,
      padImage: true,
      onRemove: resetSingleCuts,
      priceIsEstimate: true,
      price: calculatePrice(singleCutItems),
      dualPrice: calculateDualPrice(singleCutItems),
      items: singleCutItems.toJS(),
      testId: planParameters.singleCut,
    }];
  }
);

export const doubleCutsSummarySelector = createSelector(
  doubleCutItemsSelector,
  (doubleCutItems) => {
    if (doubleCutItems.size === 0) return [];
    return [{
      title: parameterMessages.doubleCuts,
      image: PictoDoubleCut,
      padImage: true,
      onRemove: resetDoubleCuts,
      price: calculatePrice(doubleCutItems),
      dualPrice: calculateDualPrice(doubleCutItems),
      items: doubleCutItems.toJS(),
      testId: planParameters.doubleCut,
    }];
  }
);

export const tripleCutsSummarySelector = createSelector(
  tripleCutItemsSelector,
  (tripleCutItems) => {
    if (tripleCutItems.size === 0) return [];
    return [{
      title: parameterMessages.tripleCuts,
      image: PictoTripleCut,
      padImage: true,
      onRemove: resetTripleCuts,
      price: calculatePrice(tripleCutItems),
      dualPrice: calculateDualPrice(tripleCutItems),
      items: tripleCutItems.toJS(),
      testId: planParameters.tripleCut,
    }];
  }
);

export const highCabinetJointsSummarySelector = createSelector(
  highCabinetJointItemsSelector,
  (highCabinetJointItems) => {
    if (highCabinetJointItems.size === 0) return [];
    return [{
      title: parameterMessages.highCabinetJoints,
      image: PictoHighCabinetJoint,
      padImage: true,
      onRemove: resetHighCabinetJoints,
      price: calculatePrice(highCabinetJointItems),
      dualPrice: calculateDualPrice(highCabinetJointItems),
      items: highCabinetJointItems.toJS(),
      testId: planParameters.highCabinetJoint,
    }];
  }
);
export const roundedCornersSummarySelector = createSelector(
  roundedCornerItemsSelector,
  (roundedCornerItems) => {
    if (roundedCornerItems.size === 0) return [];
    return [{
      title: parameterMessages.roundedCorners,
      image: PictoRoundCorner,
      padImage: true,
      onRemove: resetRoundedCorners,
      priceIsEstimate: true,
      price: calculatePrice(roundedCornerItems),
      dualPrice: calculateDualPrice(roundedCornerItems),
      items: roundedCornerItems.toJS(),
      testId: planParameters.roundedCorner,
    }];
  }
);
export const invertedCornersSummarySelector = createSelector(
  invertedCornerItemsSelector,
  (invertedCornerItems) => {
    if (invertedCornerItems.size === 0) return [];
    return [{
      title: parameterMessages.invertedCorners,
      image: PictoInnerRounding,
      padImage: true,
      onRemove: resetInvertedCorners,
      priceIsEstimate: true,
      price: calculatePrice(invertedCornerItems),
      dualPrice: calculateDualPrice(invertedCornerItems),
      items: invertedCornerItems.toJS(),
      testId: planParameters.invertedCorner,
    }];
  }
);
export const tapHolesSummarySelector = createSelector(
  tapHoleItemsSelector,
  (tapHoleItems) => {
    if (tapHoleItems.size === 0) return [];
    return [{
      title: parameterMessages.tapHoles,
      image: PictoTapHole,
      padImage: true,
      onRemove: resetTapHoles,
      price: calculatePrice(tapHoleItems),
      dualPrice: calculateDualPrice(tapHoleItems),
      items: tapHoleItems.toJS(),
      testId: 'tapHoles',
    }];
  }
);

export const drainerGroovesSummarySelector = createSelector(
  drainerGroovesItemsSelector,
  (drainerGroovesItems) => {
    if (drainerGroovesItems.length === 0) return [];
    return [{
      title: parameterMessages.drainerGrooves,
      image: PictoDrainerGrooves,
      padImage: true,
      onRemove: () => selectDrainerGrooves('none'),
      price: calculatePrice(drainerGroovesItems),
      dualPrice: calculateDualPrice(drainerGroovesItems),
      items: drainerGroovesItems.toJS(),
      testId: planParameters.drainerGrooves,
    }];
  }
);

export const wallPanelRoundCutsSmallSummarySelector = createSelector(
  wallPanelRoundCutSmallItemsSelector,
  configurationSelector,
  (wallPanelRoundCutsSmallItems, configuration) => {
    if (wallPanelRoundCutsSmallItems.size === 0) return [];
    const useImperial = configuration.get('useImperialUnitMeasurementsInTheUI');
    return [{
      title: useImperial ? [splashbackMessages.wallPanel, ' - ', parameterMessages.roundCutsSmallImperial] : [splashbackMessages.wallPanel, ' - ', parameterMessages.roundCutsSmall],
      image: PictoWallPanelRoundSmall,
      padImage: true,
      onRemove: resetWallPanelRoundCutsSmall,
      price: calculatePrice(wallPanelRoundCutsSmallItems),
      dualPrice: calculateDualPrice(wallPanelRoundCutsSmallItems),
      items: wallPanelRoundCutsSmallItems.toJS(),
      testId: planParameters.wallPanelRoundCutSmall,
    }];
  }
);

export const wallPanelRoundCutsBigSummarySelector = createSelector(
  wallPanelRoundCutBigItemsSelector,
  configurationSelector,
  (wallPanelRoundCutsBigItems, configuration) => {
    if (wallPanelRoundCutsBigItems.size === 0) return [];
    const useImperial = configuration.get('useImperialUnitMeasurementsInTheUI');
    return [{
      title: useImperial ? [splashbackMessages.wallPanel, ' - ', parameterMessages.roundCutsBigImperial] : [splashbackMessages.wallPanel, ' - ', parameterMessages.roundCutsBig],
      image: PictoWallPanelRoundBig,
      padImage: true,
      onRemove: resetWallPanelRoundCutsBig,
      price: calculatePrice(wallPanelRoundCutsBigItems),
      dualPrice: calculateDualPrice(wallPanelRoundCutsBigItems),
      items: wallPanelRoundCutsBigItems.toJS(),
      testId: planParameters.wallPanelRoundCutBig,
    }];
  }
);

export const wallPanelSquareCutsSummarySelector = createSelector(
  wallPanelSquareCutItemsSelector,
  configurationSelector,
  (wallPanelSquareCutsItems, configuration) => {
    if (wallPanelSquareCutsItems.size === 0) return [];
    const useImperial = configuration.get('useImperialUnitMeasurementsInTheUI');
    return [{
      title: useImperial ? [splashbackMessages.wallPanel, ' - ', parameterMessages.squareCutsImperial] : [splashbackMessages.wallPanel, ' - ', parameterMessages.squareCuts],
      image: PictoWallPanelSquare,
      padImage: true,
      onRemove: resetWallPanelSquareCuts,
      price: calculatePrice(wallPanelSquareCutsItems),
      dualPrice: calculateDualPrice(wallPanelSquareCutsItems),
      items: wallPanelSquareCutsItems.toJS(),
      testId: planParameters.wallPanelSquareCut,
    }];
  }
);

export const wallPanelSingleCutsSummarySelector = createSelector(
  wallPanelSingleCutItemsSelector,
  (wallPanelSingleCutsItems) => {
    if (wallPanelSingleCutsItems.size === 0) return [];
    return [{
      title: [splashbackMessages.wallPanel, ' - ', parameterMessages.singleCuts],
      image: PictoWallPanelSingle,
      padImage: true,
      onRemove: resetWallPanelSingleCuts,
      price: calculatePrice(wallPanelSingleCutsItems),
      dualPrice: calculateDualPrice(wallPanelSingleCutsItems),
      items: wallPanelSingleCutsItems.toJS(),
      testId: planParameters.wallPanelSingleCut,
    }];
  }
);

export const wallPanelDoubleCutsSummarySelector = createSelector(
  wallPanelDoubleCutItemsSelector,
  (wallPanelDoubleCutsItems) => {
    if (wallPanelDoubleCutsItems.size === 0) return [];
    return [{
      title: [splashbackMessages.wallPanel, ' - ', parameterMessages.doubleCuts],
      image: PictoWallPanelDouble,
      padImage: true,
      onRemove: resetWallPanelDoubleCuts,
      price: calculatePrice(wallPanelDoubleCutsItems),
      dualPrice: calculateDualPrice(wallPanelDoubleCutsItems),
      items: wallPanelDoubleCutsItems.toJS(),
      testId: planParameters.wallPanelDoubleCut,
    }];
  }
);

export const wallPanelTripleCutsSummarySelector = createSelector(
  wallPanelTripleCutItemsSelector,
  (wallPanelTripleCutsItems) => {
    if (wallPanelTripleCutsItems.size === 0) return [];
    return [{
      title: [splashbackMessages.wallPanel, ' - ', parameterMessages.tripleCuts],
      image: PictoWallPanelTriple,
      padImage: true,
      onRemove: resetWallPanelTripleCuts,
      price: calculatePrice(wallPanelTripleCutsItems),
      dualPrice: calculateDualPrice(wallPanelTripleCutsItems),
      items: wallPanelTripleCutsItems.toJS(),
      testId: planParameters.wallPanelTripleCut,
    }];
  }
);

export const kitchenIslandSingleCutsSummarySelector = createSelector(
  kitchenIslandSingleCutsItemsSelector,
  (kitchenIslandSingleCutItems) => {
    if (kitchenIslandSingleCutItems.size === 0) return [];
    return [{
      title: [parameterMessages.kitchenIsland, ' - ', parameterMessages.singleCuts],
      image: PictoSingleCuts,
      padImage: true,
      onRemove: resetKitchenIslandSingleCuts,
      price: calculatePrice(kitchenIslandSingleCutItems),
      dualPrice: calculateDualPrice(kitchenIslandSingleCutItems),
      items: kitchenIslandSingleCutItems.toJS(),
      testId: planParameters.kitchenIslandSingleCut,
    }];
  }
);

export const kitchenIslandRoundedCornersSummarySelector = createSelector(
  kitchenIslandRoundedCornersItemsSelector,
  (kitchenIslandRoundedCornerItems) => {
    if (kitchenIslandRoundedCornerItems.size === 0) return [];
    return [{
      title: [parameterMessages.kitchenIsland, ' - ', parameterMessages.roundedCorners],
      image: PictoRoundCorner,
      padImage: true,
      onRemove: resetKitchenIslandRoundedCorners,
      price: calculatePrice(kitchenIslandRoundedCornerItems),
      dualPrice: calculateDualPrice(kitchenIslandRoundedCornerItems),
      items: kitchenIslandRoundedCornerItems.toJS(),
      testId: planParameters.kitchenIslandRoundedCorner,
    }];
  }
);

export const kitchenIslandSquareCutsSummarySelector = createSelector(
  kitchenIslandSquareCutsItemsSelector,
  (kitchenIslandSquareCutsItems) => {
    if (kitchenIslandSquareCutsItems.size === 0) return [];
    return [{
      title: [parameterMessages.kitchenIsland, ' - ', parameterMessages.cutout],
      image: PictoCutoutsHob,
      padImage: true,
      onRemove: resetKitchenIslandSquareCuts,
      price: calculatePrice(kitchenIslandSquareCutsItems),
      dualPrice: calculateDualPrice(kitchenIslandSquareCutsItems),
      items: kitchenIslandSquareCutsItems.toJS(),
      testId: planParameters.kitchenIslandSquareCut,
    }];
  }
);

export const worktopWithLinearItem1or3Selector = createSelector(
  worktopItemsSelector,
  (selectedWorktop) => {
    let worktopLinearItem1or3 = false;
    selectedWorktop.map(worktopSection => {
      let minDepth;
      let maxDepth;
      if (!worktopSection.getIn(['item', 'ItemMeasureList', 'ItemMeasure'])){
        return worktopLinearItem1or3;
      };
      const measurements = worktopSection.getIn(['item', 'ItemMeasureList', 'ItemMeasure']);
      measurements.map(msmt => {
        const msmtType = msmt.get('ItemMeasureTypeNameEn');
        if (msmtType === 'Min. depth') {
          minDepth = msmt.get('ItemMeasureValue');
        } else if (msmtType === 'Max. depth') {
          maxDepth = msmt.get('ItemMeasureValue');
        }
      });

      if (minDepth && maxDepth) {
        if (minDepth >= 0 && maxDepth <= 450) {
          worktopLinearItem1or3 = true;
        } if (minDepth >= 636 && maxDepth <= 1500) {
          worktopLinearItem1or3 = true;
        }
      }
    });
    return worktopLinearItem1or3;
  }
);

export const kitchenIslandwithLinearItem1or3Selector = createSelector(
  kitchenIslandWorktopItemsSelector,
  (selectedWorktop) => {
    let worktopLinearItem1or3 = false;
    selectedWorktop.map(worktopSection => {
      let minDepth;
      let maxDepth;
      if (!worktopSection.getIn(['item', 'ItemMeasureList', 'ItemMeasure'])){
        return worktopLinearItem1or3;
      };
      const measurements = worktopSection.getIn(['item', 'ItemMeasureList', 'ItemMeasure']);
      measurements.map(msmt => {
        const msmtType = msmt.get('ItemMeasureTypeNameEn');
        if (msmtType === 'Min. depth') {
          console.log("msmt is: ", msmt.get('ItemMeasureValue'));
          minDepth = msmt.get('ItemMeasureValue');
        } else if (msmtType === 'Max. depth') {
          maxDepth = msmt.get('ItemMeasureValue');
        }
      });

      if (minDepth && maxDepth) {
        if (minDepth >= 0 && maxDepth <= 450) {
          worktopLinearItem1or3 = true;
        } if (minDepth >= 636 && maxDepth <= 1500) {
          worktopLinearItem1or3 = true;
        }
      }
    });
    return worktopLinearItem1or3;
  }
);

export const summarySelector = createSelector(
  worktopSummarySelector,
  sinkSummarySelector,
  cutoutsSummarySelector,
  freestandingHobsSummarySelector,
  singleCutsSummarySelector,
  doubleCutsSummarySelector,
  tripleCutsSummarySelector,
  highCabinetJointsSummarySelector,
  roundedCornersSummarySelector,
  invertedCornersSummarySelector,
  tapHolesSummarySelector,
  drainerGroovesSummarySelector,
  kitchenIslandSummarySelector,
  kitchenIslandSingleCutsSummarySelector,
  kitchenIslandRoundedCornersSummarySelector,
  kitchenIslandSquareCutsSummarySelector,
  splashbackSummarySelector,
  wallPanelRoundCutsSmallSummarySelector,
  wallPanelRoundCutsBigSummarySelector,
  wallPanelSquareCutsSummarySelector,
  wallPanelSingleCutsSummarySelector,
  wallPanelDoubleCutsSummarySelector,
  wallPanelTripleCutsSummarySelector,
  (...summaries) => summaries.reduce((result, summary) => result.concat(summary)).map(groupAndFilterSummaryItems)
);

export function groupAndFilterSummaryItems(summary) {
  return {
    ...summary,
    items: groupAndFilterItems(summary.items, summary.splitEachItemInSummary),
  };
}

export const totalImperialPriceSelector = createSelector(
  itemListSelector,
  configurationSelector,
  (itemList, configuration) => calculatePrice(itemList, 'price', configuration.get('useImperialUnitMeasurementsInTheUI'))
);

export const totalImperialInclFamilyPriceSelector = createSelector(
  itemListSelector,
  configurationSelector,
  (itemList, configuration) => calculatePriceInclFamilyPrice(itemList, 'familyPrice', configuration.get('useImperialUnitMeasurementsInTheUI'))
);


function sortByCustomSortOrder(itemA, itemB) {
  if (!itemA.sortOrder) {
    return 1;
  } else if (!itemB.sortOrder) {
    return -1;
  } else {
    return itemA.sortOrder - itemB.sortOrder;
  }
}

export function groupAndFilterItems(items, splitItems) {
  if (splitItems) {
    return mapValues((s) => {
        return s.reduce((prev, {
          amount,
          ...rest
        }) => ({
          ...rest,
          amount: amount + prev.amount
        }), {
          item: s.item,
          amount: 0,
          sortOrder: 0
        });
      }),
      values,
      filter(({
        amount
      }) => amount > 0)(items)
      .sort(sortByCustomSortOrder);
  } else {
    return flow(groupBy(({
      item
    }) => item.ItemNo),
    mapValues((s) => {
      return s.reduce((prev, {
        amount,
        ...rest
      }) => ({ ...rest,
        amount: amount + prev.amount
      }), {
        item: s.item,
        amount: 0,
        sortOrder: 0
      });
    }),
    values,
    filter(({
      amount
    }) => amount > 0))(items)
  .sort(sortByCustomSortOrder);
  }
}

export const totalPriceSelector = createSelector(
  itemListSelector,
  calculatePrice
);

export const totalDualPriceSelector = createSelector(
  itemListSelector,
  calculateDualPrice
);

export const totalPriceInclFamilyPriceSelector = createSelector(
  itemListSelector,
  calculatePriceInclFamilyPrice
);

export const totalDualPriceInclFamilyPriceSelector = createSelector(
  itemListSelector,
  calculateDualPriceInclFamilyPrice
);

export const isFamilyPriceItemSelectedSelector = createSelector(
  itemListSelector,
  configurationSelector,
  isFamilyPriceSelected
);

export const isDualFamilyPriceItemSelectedSelector = createSelector(
  itemListSelector,
  configurationSelector,
  isDualFamilyPriceSelected
);

export const totalPriceExclTaxSelector = createSelector(
  itemListSelector,
  (itemList) => calculatePrice(itemList, 'priceExclTax')
);

export const ecoFeeSelector = createSelector(
  itemListSelector,
  (itemList) => calculateFees(itemList, 'ECO', 'RetailItemCommFeeList')
);

export function calculateFees(itemList,feeTypeCode, field = 'RetailItemCommFeeList') {
  if (itemList !== undefined && itemList.find((item) => item.get('error'))) {
    return undefined;
  }
  let totalFees = 0;
  itemList.forEach((item)=>{
    if(item.get('amount')!== null && item.get('amount')!==undefined && item.get('amount') > 0){
      if(item.getIn(['item', field,'RetailItemCommFee'])){
        item.getIn(['item', field]).get('RetailItemCommFee').forEach((fee)=>{
          if(fee.get('Type') === feeTypeCode){
            totalFees = totalFees + (item.get('amount') * fee.get('Amount'));
          }
        });
      }
    }
  });
  return totalFees;
}

export function calculatePrice(itemList, field = 'price') {
  if (itemList !== undefined && itemList.find((item) => item.get('error'))) {
    return undefined;
  }
  return itemList.reduce((totalPrice, item) => totalPrice + (item.get('amount') * item.getIn(['item', field])), 0);
}

export function calculateDualPrice(itemList, field = 'dualPrice') {
  if (itemList !== undefined && itemList.find((item) => item.get('error'))) {
    return undefined;
  }
  return itemList.reduce((totalPrice, item) => totalPrice + (item.get('amount') * item.getIn(['item', field])), 0);
}

export function calculatePriceInclFamilyPrice(itemList, field = 'familyPrice') {
  if (itemList !== undefined && itemList.find((item) => item.get('error'))) {
    return undefined;
  }
  return itemList.reduce((totalPrice, item) => {
    field = 'familyPrice';
    if (item.getIn(['item', field]) && item.getIn(['item', 'RetailItemCommFamilyPriceList', 'RetailItemCommFamilyPrice', 0, 'ValidFromDateTime']) && item.getIn(['item', 'RetailItemCommFamilyPriceList', 'RetailItemCommFamilyPrice', 0, 'ValidToDateTime'])) {
      return totalPrice + (item.get('amount') * item.getIn(['item', field]));
    } else {
      field = 'price';
      return totalPrice + (item.get('amount') * item.getIn(['item', field]));
    }
  }, 0);
}

export function calculateDualPriceInclFamilyPrice(itemList, field = 'dualFamilyPrice') {
  if (itemList !== undefined && itemList.find((item) => item.get('error'))) {
    return undefined;
  }
  return itemList.reduce((totalPrice, item) => {
    field = 'dualFamilyPrice';
    if (item.getIn(['item', field]) && item.getIn(['item', 'RetailItemCommFamilyPriceList', 'RetailItemCommFamilyPrice', 0, 'ValidFromDateTime']) && item.getIn(['item', 'RetailItemCommFamilyPriceList', 'RetailItemCommFamilyPrice', 0, 'ValidToDateTime'])) {
      return totalPrice + (item.get('amount') * item.getIn(['item', field]));
    } else {
      field = 'price';
      return totalPrice + (item.get('amount') * item.getIn(['item', field]));
    }
  }, 0);
}


export function isFamilyPriceSelected(itemList, configuration, field = 'familyPrice') {
  if (configuration.get('useFamilyPrice')) {
    if (itemList !== undefined && itemList.find((item) => item.get('error'))) {
      return undefined;
    }
    if (itemList.find((item) => (item.getIn(['item', field]) && item.getIn(['item', 'RetailItemCommFamilyPriceList', 'RetailItemCommFamilyPrice', 0, 'ValidFromDateTime']) &&
    item.getIn(['item', 'RetailItemCommFamilyPriceList', 'RetailItemCommFamilyPrice', 0, 'ValidToDateTime'])) && item.get('amount') > 0)) {
      return true;
    } else {
      return false;
    }
  }
  return false;
}

export function isDualFamilyPriceSelected(itemList, configuration, field = 'familyPrice') {
  if (configuration.get('useFamilyPrice')) {
    if (itemList !== undefined && itemList.find((item) => item.get('error'))) {
      return undefined;
    }
    if (itemList.find((item) => (item.getIn(['item', field]) && item.getIn(['item', 'RetailItemCommFamilyPriceList', 'RetailItemCommFamilyPrice', 0, 'ValidFromDateTime']) &&
    item.getIn(['item', 'RetailItemCommFamilyPriceList', 'RetailItemCommFamilyPrice', 0, 'ValidToDateTime'])) && item.get('amount') > 0)) {
      return true;
    } else {
      return false;
    }
  }
  return false;
}

export function expandItemListToArticles(itemList) {
  return _.flatMap(itemList, (entry) => {
    if (entry.item.articles) {
      return entry.item.articles.map(({
        amount,
        item
      }) => ({
        amount: amount * entry.amount,
        item,
      }));
    } else {
      return [entry];
    }
  });
}

export const isQuartzAndCeramicDecentralized = ( isDecentralized, selectedMaterialType ) => {
  if(isDecentralized && (['quartz','ceramic'].includes(selectedMaterialType))){
    return true;
  }
  return false;
}
